import React, { FC, useContext, useEffect, useRef } from 'react';
import Text, { TEXT } from 'src/view/components/Text/Text';
import AddButton from 'src/view/components/Controls/AddButton/AddButton';
import { ILangTextValidation, TTranslate } from 'src/types/main';
import TableListRow from 'src/view/containers/Modals/DineInSectionModal/components/TableListRow/TableListRow';
import { LOCALES } from 'src/constants/main';
import { RestrictionAccessContext } from 'src/view/containers/Modals/DineInSectionModal/DineInSectionModal';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import { TOOLTIP_POSITION } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import './style.scss';

interface IProps {
  tables: { id: number; name: TTranslate }[];
  namesValidation: { id: number; nameValidation: ILangTextValidation }[];
  onAddTable: () => void;
  onDeleteTable: (id: number) => void;
  onChangeTableName: (tableId: number, value: string, locale: LOCALES) => void;
}

const TablesList: FC<IProps> = ({ tables, namesValidation, onAddTable, onDeleteTable, onChangeTableName }): JSX.Element => {
  const { restrictionAccess, isDisabledForEditing } = useContext(RestrictionAccessContext);

  const numberOfTables = useRef<number>(tables.length);
  const bottomAreaRef = useRef<null | HTMLDivElement>(null);

  useEffect(() => {
    // scroll to the bottom if a new table was added to list
    if (tables.length > numberOfTables.current) {
      bottomAreaRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    numberOfTables.current = tables.length;
  }, [tables.length]);

  const areTablesRemovable = tables.length > 1;

  return (
    <div className="d-flex flex-column mt-6">
      <Text text="tables" type={TEXT.SECTION_HEADER} className="mb-4" required />
      <div className="d-flex flex-column gap-15">
        {tables.map((table) => {
          const { nameValidation } = namesValidation.find((validatedTable) => validatedTable.id === table.id) || {};
          return (
            <TableListRow
              key={table.id}
              id={table.id}
              name={table.name}
              validation={nameValidation}
              showDeleteBtn={areTablesRemovable}
              onDelete={onDeleteTable}
              onChangeName={onChangeTableName}
            />
          );
        })}
      </div>

      <IntegrationManager
        setting={restrictionAccess}
        withTooltip
        tooltipOptions={{
          show: isDisabledForEditing,
          minWidth: 355,
          wrapperClassName: 'w-fit-content align-self-center',
          position: TOOLTIP_POSITION.PORTAL,
        }}
      >
        <AddButton title="add_new_table_button" className="align-self-center mt-4" onAdd={onAddTable} />
      </IntegrationManager>

      {/* space area at the bottom to fit save btn in view */}
      <div ref={bottomAreaRef} className="tables-bottom-blank" />
    </div>
  );
};

export default TablesList;
